import type { Editor } from '@tiptap/core'

/* The local, non-Yjs island's controller — the shape 2.b's Accept-with-edits /
 * Restore read to source content from an open island (design §2.a). Registered
 * by the IslandEditor on mount. The island's live content exists only as its
 * local editor's node state and never enters the shared doc, so accept reads it
 * HERE, directly from the controller, not from the ProseMirror document. */
export interface IslandController {
  // Current island HTML — what Accept-with-edits (2.b) inserts through the
  // existing accept pipeline (which already reads `editedContent ?? op.content`).
  getCurrentContent: () => string
  // The AI op's unmodified seed HTML — what Restore original (2.b) reverts to.
  getOriginalContent: () => string
  // Programmatic close (Restore/Reject in 2.b; the island's own Done in 2.a).
  close: () => void
  /* Flush the in-progress draft to editedContent NOW, synchronously (invariant
   * 6). Self-guarding: a no-op unless the clinician has actually edited since
   * mount/restore (dirty) and has not already decided the suggestion. Idempotent
   * — a second call after a flush writes nothing. The conflict/whole-doc-replace
   * reconciler calls this (via flushOpenIslands) before repositioning or
   * dropping proposals, so a mid-edit draft is persisted rather than lost. */
  flushEdited: () => void
}

/* Per-editor registry of which suggestion islands are open for editing plus
 * their live controllers. Island open/edit state lives OUTSIDE the ProseMirror
 * plugin on purpose: an island is local and never enters the shared doc, so its
 * state is not document state. The popover (open/close), the widget
 * (subscribe + swap preview↔island), and the accept pipeline (getController,
 * 2.b) all reach one instance off the host editor's AIProposalsExtension
 * storage. */
export interface IslandRegistry {
  /* Edit-mode intent. `caretPos` is where the clinician clicked, already mapped
   * to a document position by the preview it was clicked on — the island opens
   * with its caret there instead of blanket-focusing the end. Omitted by any
   * non-positional open (a bare programmatic open, tests), which falls back to
   * the end. */
  open: (id: string, caretPos?: number) => void
  close: (id: string) => void
  isOpen: (id: string) => boolean
  /* The caret position handed to the most recent open, read once by the island
   * as it mounts. Cleared on close so a later positionless open cannot inherit
   * a stale coordinate. */
  getCaretPos: (id: string) => number | undefined
  // Controller lifecycle — the IslandEditor registers on mount, clears on unmount.
  registerController: (id: string, controller: IslandController) => void
  unregisterController: (id: string) => void
  getController: (id: string) => IslandController | undefined
  /* React subscription (useSyncExternalStore): `subscribe` fires on any
   * open/close change; `getOpenSnapshot(id)` is the per-widget boolean snapshot
   * that drives the preview↔island swap. A boolean is a stable primitive, so
   * Object.is snapshot comparison never loops. */
  subscribe: (listener: () => void) => () => void
  getOpenSnapshot: (id: string) => boolean
  /* Flush every OPEN island's in-progress draft to editedContent (invariant 6).
   * The conflict/whole-doc-replace reconciler calls this before repositioning or
   * dropping proposals so a clinician's mid-edit island survives a collaborative
   * conflict. Each island's flush is self-guarding (dirty-and-not-decided only),
   * so a clean island is a no-op. */
  flushOpenIslands: () => void
}

export interface AIProposalsStorage {
  islandRegistry: IslandRegistry
}

export function createIslandRegistry(): IslandRegistry {
  const openIds = new Set<string>()
  const caretPositions = new Map<string, number>()
  const controllers = new Map<string, IslandController>()
  const listeners = new Set<() => void>()

  const emit = (): void => {
    for (const listener of listeners) {
      listener()
    }
  }

  return {
    open(id, caretPos) {
      if (caretPos === undefined) {
        caretPositions.delete(id)
      } else {
        caretPositions.set(id, caretPos)
      }
      if (!openIds.has(id)) {
        openIds.add(id)
        emit()
      }
    },
    close(id) {
      caretPositions.delete(id)
      if (openIds.delete(id)) {
        emit()
      }
    },
    isOpen: (id) => openIds.has(id),
    getCaretPos: (id) => caretPositions.get(id),
    registerController(id, controller) {
      controllers.set(id, controller)
    },
    unregisterController(id) {
      controllers.delete(id)
    },
    getController: (id) => controllers.get(id),
    flushOpenIslands() {
      for (const id of openIds) {
        controllers.get(id)?.flushEdited()
      }
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getOpenSnapshot: (id) => openIds.has(id),
  }
}

/* Read the island registry off an editor's AIProposalsExtension storage. Null on
 * an editor that has no AIProposalsExtension (bare/non-note editors). */
export function getIslandRegistry(editor: Editor): IslandRegistry | null {
  return editor.storage.aiProposals?.islandRegistry ?? null
}
